/**
 * 教师批注面板：从「超长勾选列表」改为「当前节点卡片 → 三方向批注」。
 *
 * - 只展示当前环节激活的 DAG 节点（最多 2 个），避免教师注意力被 34 条论据淹没；
 * - 每张节点卡给出 2–3 个方向（关联节点/论据/摘要），点一下预填批注；
 * - 仍保留自由 textarea 与 `annotation-input/submit/item` testid 兼容既有测试。
 */
import { useMemo, useRef, useState } from 'react';
import type { MotionPackage } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, Button, EmptyState } from '../ui/primitives.tsx';
import { formatTimeOfDay } from '../../export/report.ts';

export function AnnotationPanel({ motion }: { motion?: MotionPackage }) {
  const { state, actions } = useArena();
  const [draft, setDraft] = useState('');
  const [selectedDirection, setSelectedDirection] = useState<{ node: string; direction: string } | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const annotations = [...state.annotations].sort((a, b) => b.at - a.at);

  const focusNodes = useMemo(() => {
    if (!motion) return [];
    const stageIndex = Math.max(1, state.stageIndex);
    const active = motion.dag.nodes.filter(
      (node) => node.stageRange[0] <= stageIndex && stageIndex <= node.stageRange[1],
    );
    // 未开赛时没有激活节点：给教师一张“首环节预览卡”，避免看起来像占位符。
    const nodes = active.length > 0 ? active : motion.dag.nodes.slice(0, 2);
    return nodes.slice(0, 2).map((node) => {
      const outgoing = motion.dag.edges
        .filter((edge) => edge.source === node.id)
        .map((edge) => motion.dag.nodes.find((candidate) => candidate.id === edge.target)?.label)
        .filter((label): label is string => Boolean(label));
      const evidence = node.evidenceIds
        .map((id) => motion.evidenceLibrary.find((item) => item.id === id)?.shortTitle)
        .filter((title): title is string => Boolean(title));
      const directions = [...outgoing.slice(0, 2), ...evidence.slice(0, 2), node.summary]
        .filter((item, index, all) => item && all.indexOf(item) === index)
        .slice(0, 3);
      return { node, directions };
    });
  }, [motion, state.stageIndex]);

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    actions.addAnnotation(
      selectedDirection ? `【${selectedDirection.node} · ${selectedDirection.direction}】${text}` : text,
    );
    setDraft('');
    setSelectedDirection(null);
  };

  const prefill = (text: string) => {
    setDraft((current) => (current ? `${current}\n${text}` : text));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <section className="lens">
      <header className="flex items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">节点批注</h2>
          <p className="mt-0.5 text-[11px] text-slate-400">
            当前环节激活的节点 · 点方向直接预填 · Ctrl/⌘+Enter 提交
          </p>
        </div>
        <Badge tone="neutral">{state.annotations.length} 条</Badge>
      </header>

      <div className="space-y-2 p-3">
        {focusNodes.length === 0 ? (
          <p className="rounded-md bg-white/[0.025] px-2.5 py-2 text-[10.5px] text-slate-500">
            尚未进入环节；开赛后这里会按当前环节显示激活节点。
          </p>
        ) : (
          focusNodes.map(({ node, directions }) => (
            <article
              key={node.id}
              data-testid="node-annotation-card"
              className="rounded-lg bg-white/[0.025] p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="micro-label">
                    {node.kind} · S{String(node.stageRange[0]).padStart(2, '0')}–
                    {String(node.stageRange[1]).padStart(2, '0')} · {node.side}
                  </div>
                  <div className="mt-0.5 truncate text-[12px] font-medium text-slate-100">
                    {node.label}
                  </div>
                </div>
                <span className="pill">激活</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {directions.map((direction) => (
                  <button
                    key={direction}
                    type="button"
                    data-testid="node-annotation-direction"
                    onClick={() => {
                      setSelectedDirection({ node: node.label, direction });
                      prefill(direction);
                    }}
                    className="max-w-full truncate rounded border border-white/10 bg-black/20 px-1.5 py-1 text-left text-[9.5px] text-slate-300 hover:border-white/25 hover:text-slate-100"
                    title={direction}
                  >
                    {direction}
                  </button>
                ))}
              </div>
            </article>
          ))
        )}

        {selectedDirection && (
          <div className="flex items-center justify-between gap-2 rounded-md border border-[#7DD3FC]/20 bg-[#7DD3FC]/[0.05] px-2 py-1.5 text-[10px] text-[#BAE6FD]" data-testid="node-annotation-selected">
            <span className="min-w-0 truncate">
              已选：{selectedDirection.node} · {selectedDirection.direction}
            </span>
            <button
              type="button"
              className="shrink-0 text-[9px] text-slate-400 hover:text-slate-100"
              onClick={() => setSelectedDirection(null)}
            >
              取消
            </button>
          </div>
        )}

        <textarea
          ref={inputRef}
          data-testid="annotation-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          rows={2}
          placeholder={`记录当前环节（${state.stageIndex > 0 ? `环节 ${state.stageIndex}` : '未开始'}）的战术点评…`}
          className="w-full resize-none rounded-lg border border-white/10 bg-black/25 px-2.5 py-2 text-xs text-slate-100 placeholder:text-slate-600 focus:border-team-aff/50 focus:outline-none"
        />
        <div className="flex items-center justify-between">
          <span className="text-[9.5px] text-slate-600">教师权威记录 · 复盘与战报同步</span>
          <div className="flex items-center gap-1.5">
            {selectedDirection && (
              <Button
                testId="node-annotation-record"
                size="sm"
                variant="secondary"
                disabled={draft.trim().length === 0}
                onClick={submit}
              >
                记录此方向
              </Button>
            )}
            <Button
              testId="annotation-submit"
              size="sm"
              variant="primary"
              disabled={draft.trim().length === 0}
              onClick={submit}
            >
              提交批注
            </Button>
          </div>
        </div>

        <div>
          {annotations.length === 0 ? (
            <EmptyState title="暂无批注" detail="比赛过程中可随时记录战术观察。" />
          ) : (
            <ul className="max-h-36 space-y-1.5 overflow-auto" data-testid="annotation-list">
              {annotations.map((annotation) => (
                <li key={annotation.id} data-testid="annotation-item" className="rounded-lg bg-black/20 px-2.5 py-1.5">
                  <div className="flex items-center justify-between gap-2 text-[10px] text-slate-500">
                    <span>
                      环节 {annotation.stageIndex > 0 ? annotation.stageIndex : '—'} ·{' '}
                      {annotation.authorName}
                    </span>
                    <span className="mono">{formatTimeOfDay(annotation.at)}</span>
                  </div>
                  <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-200">{annotation.text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
